import React, { useState, useEffect } from 'react';
import { Container, Typography, Tooltip, Box, Grid, TextField, InputAdornment, useTheme, alpha, Stack, Paper, Button, Avatar, AvatarGroup } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import CodeIcon from '@mui/icons-material/Code';
import PublicIcon from '@mui/icons-material/Public';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PaletteIcon from '@mui/icons-material/Palette';
import BuildIcon from '@mui/icons-material/Build';
import ApiIcon from '@mui/icons-material/Api';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import BoltIcon from '@mui/icons-material/Bolt';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupIcon from '@mui/icons-material/Group';
import VerifiedIcon from '@mui/icons-material/Verified';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const toolCategories = [
  {
    title: 'Geospatial & Map',
    tools: [
      {
        title: 'Spatial Tools',
        description: 'Geospatial data on interactive maps',
        path: '/spatial-tools-app',
        icon: <PublicIcon sx={{ fontSize: 32 }} />,
      }
    ]
  },
  {
    title: 'Data & Formatting',
    tools: [
      {
        title: 'JSON Tools',
        description: 'Validate, format, and convert JSON data',
        path: '/json-tools',
        icon: <CodeIcon sx={{ fontSize: 32 }} />,
      },
      {
        title: 'Text Tools',
        description: 'Text manipulation and encoding',
        path: '/text-tools-app',
        icon: <TextFieldsIcon sx={{ fontSize: 32 }} />,
      },
      {
        title: 'Color & Image',
        description: 'Color conversion and image tools',
        path: '/color-image-tools',
        icon: <PaletteIcon sx={{ fontSize: 32 }} />,
      }
    ]
  },
  {
    title: 'Network & Developer',
    tools: [
      {
        title: 'Mailinator',
        description: 'Temporary inboxes for instant email testing',
        path: '/mailinator',
        icon: <EmailIcon sx={{ fontSize: 32 }} />,
      },
      {
        title: 'API Tools',
        description: 'Test and debug API endpoints',
        path: '/api-tools',
        icon: <ApiIcon sx={{ fontSize: 32 }} />,
      },
      {
        title: 'Dev Tools',
        description: 'Essential developer utilities',
        path: '/dev-tools',
        icon: <BuildIcon sx={{ fontSize: 32 }} />,
      }
    ]
  }
];

const stats = [
  { label: 'Tools Available', value: '50+', icon: <BoltIcon /> },
  { label: 'Lines Processed', value: '1.2M+', icon: <SpeedIcon /> },
  { label: 'Happy Developers', value: '10k+', icon: <GroupIcon /> },
  { label: 'Uptime', value: '99.9%', icon: <VerifiedIcon /> },
];

const quickActions = [
  { label: 'Format JSON', path: '/json-tools' },
  { label: 'Generate UUID', path: '/dev-tools' },
  { label: 'Test Webhook', path: '/api-tools' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100, damping: 15 } }
};

function Home() {
  const [searchQuery, setSearchQuery] = useState('');
  const [recentTools, setRecentTools] = useState([]);
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    try {
      const stored = localStorage.getItem('umbrella_recent_tools');
      if (stored) {
        const paths = JSON.parse(stored);
        const allTools = toolCategories.flatMap(cat => cat.tools);
        const resolvedTools = paths.map(p => allTools.find(t => t.path === p)).filter(Boolean);
        setRecentTools(resolvedTools);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const filteredCategories = toolCategories.map(cat => ({
    ...cat,
    tools: cat.tools.filter(tool =>
      tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => cat.tools.length > 0);

  return (
    <Box sx={{ overflow: 'hidden', bgcolor: 'background.default', minHeight: '100vh' }}>
      <SEO
        title="UmbrellaCo - The Ultimate Developer Toolkit"
        description="A comprehensive suite of powerful, fast, and intuitive tools for modern development needs."
        name="UmbrellaCo"
        type="website"
      />

      {/* Hero Section with Command Palette */}
      <Box
        sx={{
          position: 'relative',
          background: isDark 
            ? 'radial-gradient(circle at 50% 0%, rgba(99, 102, 241, 0.15) 0%, transparent 70%)'
            : `radial-gradient(circle at 50% 0%, ${alpha(theme.palette.primary.light, 0.2)} 0%, transparent 70%)`,
          pt: { xs: 8, md: 14 },
          pb: { xs: 8, md: 10 },
          textAlign: 'center',
          overflow: 'hidden',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 900, letterSpacing: 4, mb: 2, display: 'block' }}>
              UMBRELLACO
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                mb: 2,
                color: 'text.primary',
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
              }}
            >
              The Swiss Army Knife <br/> for Modern Devs.
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
                lineHeight: 1.6,
                mb: 6,
                fontSize: { xs: '1rem', md: '1.2rem' }
              }}
            >
              Zero server uploads. 100% privacy. Lightning fast. <br/> Everything you need to format, convert, and visualize data.
            </Typography>
          </motion.div>

          {/* Command Palette / Main Search */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <Box sx={{ maxWidth: 650, mx: 'auto', position: 'relative' }}>
              <TextField
                fullWidth
                placeholder="Search tools, formats, or hit '/'"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 24, ml: 1 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ 
                        px: 1.5, py: 0.5, 
                        borderRadius: 1.5, 
                        bgcolor: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.05),
                        border: `1px solid ${isDark ? alpha('#fff', 0.2) : alpha('#000', 0.1)}`,
                        fontSize: '0.75rem',
                        fontWeight: 800,
                        color: 'text.secondary'
                      }}>
                        /
                      </Box>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 50,
                    bgcolor: isDark ? alpha('#000', 0.4) : '#fff',
                    backdropFilter: 'blur(10px)',
                    fontSize: '1.1rem',
                    py: 1,
                    boxShadow: theme.shadows[isDark ? 0 : 4],
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    transition: 'all 0.3s ease',
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      boxShadow: theme.shadows[8],
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
                      border: `1px solid ${theme.palette.primary.main}`,
                    },
                  }
                }}
              />
              
              {/* Subtle Suggested Tags */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', fontWeight: 600, mr: 1 }}>
                  Suggested:
                </Typography>
                {quickActions.map((action, idx) => (
                  <Button 
                    key={idx}
                    variant="outlined" 
                    size="small"
                    onClick={() => navigate(action.path)}
                    sx={{ 
                      borderRadius: 50, 
                      textTransform: 'none', 
                      fontWeight: 600,
                      borderColor: alpha(theme.palette.divider, 0.2),
                      color: 'text.secondary',
                      '&:hover': {
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Floating Live Stats Row */}
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 10, mb: 8 }}>
        <Paper 
          elevation={isDark ? 0 : 4}
          sx={{ 
            py: 4, 
            borderRadius: 4,
            bgcolor: isDark ? '#1e1e2d' : '#fff',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Grid container spacing={4} justifyContent="center" alignItems="center">
            {stats.map((stat, idx) => (
              <Grid item xs={6} md={3} key={idx} sx={{ 
                textAlign: 'center',
                position: 'relative',
                '&:not(:last-child)::after': {
                  content: '""',
                  position: 'absolute',
                  right: 0,
                  top: '20%',
                  height: '60%',
                  width: '1px',
                  bgcolor: alpha(theme.palette.divider, 0.1),
                  display: { xs: 'none', md: 'block' }
                }
              }}>
                <Box sx={{ color: 'primary.main', mb: 1, display: 'flex', justifyContent: 'center' }}>
                  {stat.icon}
                </Box>
                <Typography variant="h4" fontWeight={800} color="text.primary">
                  {stat.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      {/* Tools Section */}
      <Container maxWidth="lg" sx={{ pb: 12 }}>
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          
          {/* Recent Workspaces */}
          {recentTools.length > 0 && searchQuery === '' && (
            <Box sx={{ width: '100%', mb: 8 }}>
              <Typography variant="h5" fontWeight={800} sx={{ mb: 3, color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                <AutoFixHighIcon sx={{ mr: 1.5, color: 'primary.main' }}/> Jump Back In
              </Typography>
              <Grid container spacing={3}>
                {recentTools.map((tool, index) => (
                  <Grid item xs={12} sm={6} md={4} key={`recent-${tool.title}`}>
                    <motion.div variants={itemVariants}>
                      <Link to={tool.path} style={{ textDecoration: 'none' }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            height: '100%',
                            bgcolor: isDark ? alpha('#fff', 0.02) : '#fff',
                            borderRadius: 4,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: theme.shadows[isDark ? 0 : 8],
                              borderColor: theme.palette.primary.main,
                              '& .tool-icon-box': { transform: 'scale(1.1)' }
                            },
                          }}
                        >
                          <Box
                            className="tool-icon-box"
                            sx={{
                              width: 48, height: 48, borderRadius: '12px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main', mb: 2,
                              transition: 'transform 0.3s ease',
                            }}
                          >
                            {tool.icon}
                          </Box>
                          <Typography variant="h6" fontWeight={800} sx={{ color: 'text.primary', mb: 1 }}>
                            {tool.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, flexGrow: 1 }}>
                            {tool.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontSize: '0.9rem', fontWeight: 700 }}>
                            Resume <ArrowForwardIcon sx={{ fontSize: 18, ml: 0.5 }} />
                          </Box>
                        </Paper>
                      </Link>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* All Categories */}
          {filteredCategories.map((category) => (
            <Box key={category.title} sx={{ width: '100%', mb: 6 }}>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 3, color: 'text.secondary', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, pb: 1 }}>
                {category.title}
              </Typography>
              <Grid container spacing={3}>
                {category.tools.map((tool, index) => (
                  <Grid item xs={12} sm={6} md={4} key={tool.title}>
                    <motion.div variants={itemVariants}>
                      <Link to={tool.path} style={{ textDecoration: 'none' }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            height: '100%',
                            bgcolor: isDark ? alpha('#fff', 0.02) : '#fff',
                            borderRadius: 4,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: theme.shadows[isDark ? 0 : 4],
                              borderColor: theme.palette.primary.main,
                              '& .tool-icon-box': { transform: 'scale(1.1)' }
                            },
                          }}
                        >
                          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Box
                              className="tool-icon-box"
                              sx={{
                                width: 40, height: 40, borderRadius: '10px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                transition: 'transform 0.3s ease',
                              }}
                            >
                              {React.cloneElement(tool.icon, { sx: { fontSize: 22 } })}
                            </Box>
                            <Typography variant="h6" fontWeight={800} sx={{ color: 'text.primary' }}>
                              {tool.title}
                            </Typography>
                          </Stack>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {tool.description}
                          </Typography>
                        </Paper>
                      </Link>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </motion.div>
      </Container>
      
    </Box>
  );
}

export default Home;
